import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxiosClient } from "../../../API/index";
import { normalizeChatHistory } from "../../Utils/dataNormalization";
import type {
  ChatMessage,
  MessageResponse,
} from "../../Components/ChatPanel/ChatThread/ChatMessage/messageTypes";

/**
 * Chat API Hooks
 *
 * Supports:
 * - GET /chats - List chats
 * - GET /chats/{chatId}/messages - Get chat messages
 * - POST /chats - Create new chat
 * - PUT /chats/{chatId}/messages/{messageId}/feedback - Submit message feedback
 *
 * Features:
 * - JWT + X-Account handled automatically (via useAxiosClient)
 * - Optimistic updates for better UX
 * - Automatic cache invalidation
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface Chat {
  chat_uuid: string;
  title: string;
  is_active: boolean;
  last_updated: string; // ISO 8601 date string
}

export interface GetChatsResponse {
  chats: Chat[];
}

export interface GetMessagesResponse {
  messages: MessageResponse[];
}

export interface CreateChatResponse {
  chat_uuid: string;
}

export interface SendMessageParams {
  chatId: string;
  content: string;
}

export const MessageVote = {
  UP: "up",
  DOWN: "down",
} as const;

export type MessageVoteValue = (typeof MessageVote)[keyof typeof MessageVote];

export interface SubmitFeedbackParams {
  chatId: string;
  messageId: string;
  vote: MessageVoteValue;
}

// ============================================================================
// Query Key Factories
// ============================================================================

export const chatKeys = {
  chats: ["chats"] as const,
  messages: (chatId: string) => [...["chats"], chatId, "messages"] as const,
  createChat: () => [...["chats"], "create"] as const,
  submitFeedback: (chatId: string, messageId: string) =>
    [...["chats"], chatId, "messages", messageId, "feedback"] as const,
};

// ============================================================================
// Query Hooks (GET requests)
// ============================================================================

/**
 * GET /chats
 *
 * Returns the list of chats for the current user.
 * Chats are sorted by last_updated descending.
 *
 * Used:
 * - On initial chat page load
 * - After chat metadata changes (e.g. via SSE)
 * - After completing a streamed response
 *
 * @example
 * ```tsx
 * const { data, isLoading, refetch } = useChats();
 * // data.items = array of chats
 * ```
 */
export const useChats = () => {
  const client = useAxiosClient({ domain: "ai" });

  return useQuery({
    queryKey: chatKeys.chats,
    queryFn: async () => {
      const response = await client.get<GetChatsResponse>("/chats");
      return response.data;
    },
    // Auto-refetch to keep chat list fresh
    staleTime: 30 * 1000, // Data is considered fresh for 30 seconds
  });
};

/**
 * GET /chats/{chatId}/messages
 *
 * Returns the list of messages for a specific chat.
 * Messages are sorted by timestamp descending.
 *
 * Used:
 * - On initial chat page load
 * - After new messages are received (via SSE)
 *
 * @example
 * ```tsx
 * const { data, isLoading, refetch } = useMessages("chat-123");
 * // data.messages = array of messages
 * ```
 */
export const useMessages = (chatId: string, shouldFetch: boolean = true) => {
  const client = useAxiosClient({ domain: "ai" });
  return useQuery({
    queryKey: chatKeys.messages(chatId),
    queryFn: async () => {
      const response = await client.get<GetMessagesResponse>(
        `/chats/${chatId}/messages`
      );
      return response.data;
    },
    enabled: !!chatId && shouldFetch,
    select: (data: GetMessagesResponse): ChatMessage[] =>
      normalizeChatHistory(data?.messages || []),
    staleTime: 1 * 1000,
  });
};

/**
 * POST /chats
 *
 * Creates a new empty chat and returns its identifier.
 * This endpoint has no request payload.
 *
 * @example
 * ```tsx
 * const { mutate: createChat, isPending } = useCreateChat();
 *
 * createChat(undefined, {
 *   onSuccess: (data) => {
 *     console.log("Created chat:", data.chatId);
 *   },
 * });
 * ```
 */
export const useCreateChat = () => {
  const client = useAxiosClient({ domain: "ai" });
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: chatKeys.createChat(),
    mutationFn: async () => {
      const response = await client.post<CreateChatResponse>("/chats");
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.chats,
      });
    },
  });
};

/**
 * PUT /chats/{chatId}/messages/{messageId}/feedback
 *
 * Submits user feedback (up/down vote) for a specific assistant message.
 * This is a fire-and-forget operation from the frontend perspective.
 *
 * When it happens:
 * - When the user interacts with feedback controls on an assistant message
 *
 * @example
 * ```tsx
 * const { mutate: submitFeedback } = useSubmitFeedback();
 *
 * submitFeedback({
 *   chatId: "chat-123",
 *   messageId: "msg-456",
 *   vote: "up"
 * });
 * ```
 */
export const useSubmitFeedback = () => {
  const client = useAxiosClient({ domain: "ai" });

  return useMutation({
    mutationKey: ["chats", "feedback"],
    mutationFn: async ({ chatId, messageId, vote }: SubmitFeedbackParams) => {
      await client.put(`/chats/${chatId}/messages/${messageId}/feedback`, {
        vote,
      });
    },
    // Fire-and-forget - no cache invalidation needed
  });
};

export default {
  useChats,
  useMessages,
  useCreateChat,
  useSubmitFeedback,
  chatKeys,
};
